// src/shared/components/Header/index.js
import React, { useRef, useEffect, useState } from 'react';
import { Navbar, Nav, Container, Image, Dropdown } from 'react-bootstrap';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../../contexts/ThemeContext';
import VersionBadge from '../VersionBadge';
import './styles.css';

function useMediaQuery(query) {
    const [matches, setMatches] = useState(false);
    useEffect(() => {
        const media = window.matchMedia(query);
        if (media.matches !== matches) setMatches(media.matches);
        const listener = (e) => setMatches(e.matches);
        media.addEventListener('change', listener);
        return () => media.removeEventListener('change', listener);
    }, [matches, query]);
    return matches;
}

const Header = () => {
    const isDesktop = useMediaQuery('(min-width: 992px)');
    const { theme, toggleTheme } = useTheme();
    const { isAuthenticated, logout, user, hasPermission } = useAuth();
    const adminPermissions = [
        'users:managePermissions',
        'activities:handleExemptions',
        'competition:create',
        'competition:edit',
    ];
    const isAdmin = adminPermissions.some(hasPermission);
    const [expanded, setExpanded] = useState(false);
    const navbarRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (navbarRef.current && !navbarRef.current.contains(event.target) && expanded) {
                setExpanded(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [expanded]);

    const handleLogout = () => {
        logout();
        setExpanded(false);
    };

    const handleNavClick = () => {
        setExpanded(false);
    };

    const getThemeIconPath = () => {
        return theme === 'dark' ? '/theme/dark_mode.png' : '/theme/light_mode.png';
    };

    return (
        <Navbar expand="lg" expanded={expanded} onToggle={setExpanded} ref={navbarRef}>
            <Container>
                <Navbar.Brand as={NavLink} to="/" onClick={handleNavClick}>
                    <Image src="/logo512.png" width="25" className="mr-2 logo-image" />
                    FitOps
                </Navbar.Brand>
                <Navbar.Toggle aria-controls="basic-navbar-nav" />
                <Navbar.Collapse id="basic-navbar-nav" className="justify-content-between">
                    {isDesktop ? (
                        <>
                            <Nav className="mr-auto">
                                {isAuthenticated && (
                                    <>
                                        <Nav.Link as={NavLink} to="/fithub" onClick={handleNavClick}>FitHub</Nav.Link>
                                        <Nav.Link as={NavLink} to="/activities" onClick={handleNavClick}>Recent Activities</Nav.Link>
                                        {/* <Nav.Link as={NavLink} to="/statistics">Statistics</Nav.Link> */}
                                    </>
                                )}
                                {!isAuthenticated && (
                                    <>
                                        <Nav.Link as={NavLink} to="/rules" onClick={handleNavClick}>Rules</Nav.Link>
                                        <Nav.Link as={NavLink} to="/help" onClick={handleNavClick}>Help</Nav.Link>
                                    </>
                                )}
                            </Nav>
                            <Nav>
                                <Nav.Link onClick={() => { toggleTheme(); handleNavClick(); }}>
                                    <Image src={getThemeIconPath()} width="25" alt="Theme toggle" />
                                </Nav.Link>
                                {isAuthenticated ? (
                                    <Dropdown>
                                        <Dropdown.Toggle variant="link" id="user-dropdown" className="user-dropdown-toggle">
                                            <Image
                                                src={user?.profilePicture || '/logo192.png'}
                                                roundedCircle
                                                style={{ width: '32px', height: '32px', objectFit: 'cover' }}
                                                alt="Profile"
                                            />
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu align="end" id="user-dropdown-menu">
                                            <Dropdown.Header>{user?.firstName} {user?.lastName}</Dropdown.Header>
                                            <Dropdown.Item as={NavLink} to="/activity" onClick={handleNavClick}>
                                                Register Activity
                                            </Dropdown.Item>
                                            <Dropdown.Item as={NavLink} to={`/profile/${user?.Username}`} onClick={handleNavClick}>
                                                My Profile
                                            </Dropdown.Item>
                                            <Dropdown.Divider />
                                            <Dropdown.Item as={NavLink} to="/competitions" onClick={handleNavClick}>
                                                Competitions
                                            </Dropdown.Item>
                                            <Dropdown.Item as={NavLink} to="/competitors" onClick={handleNavClick}>
                                                Competitors
                                            </Dropdown.Item>
                                            <Dropdown.Item as={NavLink} to="/rules" onClick={handleNavClick}>
                                                Rules
                                            </Dropdown.Item>
                                            <Dropdown.Item as={NavLink} to="/help" onClick={handleNavClick}>
                                                Help
                                            </Dropdown.Item>
                                            {isAdmin && (
                                                <Dropdown.Item as={NavLink} to="/admin" onClick={handleNavClick}>
                                                    <i className="bi bi-shield-lock me-2"></i>Admin
                                                </Dropdown.Item>
                                            )}
                                            <Dropdown.Divider />
                                            <Dropdown.Item onClick={handleLogout}>Logout</Dropdown.Item>
                                        </Dropdown.Menu>
                                    </Dropdown>
                                ) : (
                                    <Nav.Link as={NavLink} to="/login" onClick={handleNavClick}>Login</Nav.Link>
                                )}
                            </Nav>
                        </>
                    ) : (
                            <Nav className="flex-column mobile-menu">
                                {isAuthenticated ? (
                                    <>
                                        {/* Profile header */}
                                        <Nav className="align-items-center mb-2 mobile-user-section">
                                            <Image
                                                src={user?.profilePicture || '/logo192.png'}
                                                roundedCircle
                                                style={{ width: '32px', height: '32px', objectFit: 'cover' }}
                                                alt="Profile"
                                                className="me-2"
                                            />
                                            <span className="fw-semibold">{user?.firstName} {user?.lastName}</span>
                                        </Nav>

                                        {/* Navigation links */}
                                        <Nav.Link as={NavLink} to="/fithub" onClick={handleNavClick}>FitHub</Nav.Link>
                                        <Nav.Link as={NavLink} to="/activities" onClick={handleNavClick}>Recent Activities</Nav.Link>



                                        {/* User actions */}
                                        <Nav.Link as={NavLink} to="/activity" onClick={handleNavClick}>Register Activity</Nav.Link>
                                        <Nav.Link as={NavLink} to={`/profile/${user?.Username}`} onClick={handleNavClick}>My Profile</Nav.Link>
                                        <Nav.Link as={NavLink} to="/competitions" onClick={handleNavClick}>Competitions</Nav.Link>
                                        <Nav.Link as={NavLink} to="/competitors" onClick={handleNavClick}>Competitors</Nav.Link>
                                        <Nav.Link as={NavLink} to="/rules" onClick={handleNavClick}>Rules</Nav.Link>
                                        <Nav.Link as={NavLink} to="/help" onClick={handleNavClick}>Help</Nav.Link>

                                        {/* Theme toggle */}
                                        <Nav.Link onClick={() => { toggleTheme(); handleNavClick(); }} className="d-flex align-items-center">
                                            <Image src={getThemeIconPath()} width="25" alt="Theme toggle" className="me-2" />
                                        </Nav.Link>

                                        {/* Admin */}
                                        {isAdmin && (
                                            <Nav.Link as={NavLink} to="/admin" onClick={handleNavClick}>
                                                <i className="bi bi-shield-lock me-2"></i>Admin
                                            </Nav.Link>
                                        )}

                                        {/* Logout */}
                                        <Nav.Link onClick={handleLogout} className="text-danger">Logout</Nav.Link>

                                        {/* Version badge */}
                                        <div className="mobile-version-container">
                                            <VersionBadge mobile />
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <Nav.Link as={NavLink} to="/rules" onClick={handleNavClick}>Rules</Nav.Link>
                                        <Nav.Link as={NavLink} to="/help" onClick={handleNavClick}>Help</Nav.Link>
                                        <Nav.Link as={NavLink} to="/login" onClick={handleNavClick}>Login</Nav.Link>

                                        {/* Version badge */}
                                        <div className="mobile-version-container">
                                            <VersionBadge mobile />
                                        </div>
                                    </>
                                )}
                            </Nav>
                    )}
                </Navbar.Collapse>
            </Container>
        </Navbar>
    );
};

export default Header;
